import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// The feed stays in the app cache, while these positions belong to browser
// history entries. They only need to live for the current tab session.
const returnPositions = new Map();
const MAX_RETURN_POSITIONS = 30;

export function getCommunityReturnPosition(locationKey) {
  return returnPositions.get(locationKey);
}

export function clearCommunityReturnPosition(locationKey) {
  returnPositions.delete(locationKey);
}

export function useCommunityScrollRestoration({ locationKey, navigationType, filter, itemCount }) {
  const itemsRef = useRef(null);
  // Reserve each card's previous height on Back so image placeholders cannot
  // shorten the document before the browser restores its scroll position.
  const returnLayoutRef = useRef(
    navigationType === 'POP' ? returnPositions.get(locationKey) : null
  );

  const getReturnItemHeight = (itemKey) => {
    const saved = returnLayoutRef.current;
    if (typeof window === 'undefined' || saved?.filter !== filter
      || saved.viewportWidth !== window.innerWidth) return undefined;
    return saved.itemHeights?.get(itemKey);
  };

  useEffect(() => {
    const clearOnInput = () => clearCommunityReturnPosition(locationKey);
    window.addEventListener('pointerdown', clearOnInput, { passive: true });
    window.addEventListener('wheel', clearOnInput, { passive: true });
    window.addEventListener('touchstart', clearOnInput, { passive: true });
    window.addEventListener('keydown', clearOnInput);
    return () => {
      window.removeEventListener('pointerdown', clearOnInput);
      window.removeEventListener('wheel', clearOnInput);
      window.removeEventListener('touchstart', clearOnInput);
      window.removeEventListener('keydown', clearOnInput);
    };
  }, [locationKey]);

  const rememberClickedItem = useCallback((event) => {
    const item = event.target.closest('.home-community__item');
    if (!item || !itemsRef.current?.contains(item)) return;

    returnPositions.delete(locationKey);
    returnPositions.set(locationKey, {
      filter,
      itemKey: item.dataset.communityItemKey,
      top: item.getBoundingClientRect().top,
      viewportWidth: window.innerWidth,
      itemHeights: new Map(Array.from(itemsRef.current.children, (child) => [
        child.dataset.communityItemKey,
        child.getBoundingClientRect().height,
      ])),
    });
    if (returnPositions.size > MAX_RETURN_POSITIONS) {
      returnPositions.delete(returnPositions.keys().next().value);
    }
  }, [filter, locationKey]);

  useLayoutEffect(() => {
    const saved = navigationType === 'POP' && returnPositions.get(locationKey);
    if (!saved || saved.filter !== filter || !itemsRef.current) return undefined;

    const item = Array.from(itemsRef.current.children).find(
      (child) => child.dataset.communityItemKey === saved.itemKey
    );
    if (!item) return undefined;

    let frame;
    let stopped = false;
    const restore = () => {
      frame = undefined;
      if (stopped || !item.isConnected) return;
      const difference = item.getBoundingClientRect().top - saved.top;
      if (Math.abs(difference) > 1) window.scrollBy(0, difference);
    };
    const scheduleRestore = () => {
      if (!stopped && frame === undefined) frame = requestAnimationFrame(restore);
    };
    const stopRestoring = () => {
      stopped = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      observer.disconnect();
    };

    // Artwork images resize their cards after remount. Keep the clicked card
    // at its original viewport position as those images finish loading.
    const observer = new ResizeObserver(scheduleRestore);
    observer.observe(itemsRef.current);
    scheduleRestore();
    window.addEventListener('wheel', stopRestoring, { passive: true, once: true });
    window.addEventListener('touchstart', stopRestoring, { passive: true, once: true });
    window.addEventListener('keydown', stopRestoring, { once: true });
    const timeout = window.setTimeout(stopRestoring, 10000);

    return () => {
      stopRestoring();
      window.clearTimeout(timeout);
      window.removeEventListener('wheel', stopRestoring);
      window.removeEventListener('touchstart', stopRestoring);
      window.removeEventListener('keydown', stopRestoring);
    };
  }, [filter, itemCount, locationKey, navigationType]);

  return { itemsRef, rememberClickedItem, getReturnItemHeight };
}
